import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationDepartment } from "../../../models/organisation/application-department";
import DepartmentService from "../../../services/organisation/department-service";
import { ApplicationError } from "../../../models/errors/application-error";
import { mapStringToEnum } from "../../../helpers/enum-helper";
import { ApplicationOrderTemplate } from "../../../models/organisation/application-order-template";
import { ApplicationSubDepartment } from "../../../models/organisation/application-sub-department";
import { ApplicationProductSelectionMode } from "../../../models/organisation/application-product-selection-mode";

interface AdminDepartmentState {
  loading: boolean;
  selectedDepartment: ApplicationDepartment | null;
  selectedSubDepartment: ApplicationSubDepartment | null;
  creatingSubDepartment: boolean;
  error: string | null;
}

const initialState: AdminDepartmentState = {
  loading: false,
  selectedDepartment: null,
  selectedSubDepartment: null,
  creatingSubDepartment: false,
  error: null,
};

// Async thunks
export const fetchDepartmentById = createAsyncThunk<
  ApplicationDepartment,
  number,
  { state: RootState }
>("departments/fetchById", async (departmentId, { rejectWithValue }) => {
  const departmentService = new DepartmentService();
  try {
    return await departmentService.getDepartmentById(departmentId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const updateDepartment = createAsyncThunk<
  ApplicationDepartment,
  ApplicationDepartment,
  { state: RootState }
>("departments/update", async (department, { rejectWithValue }) => {
  const departmentService = new DepartmentService();
  try {
    return await departmentService.updateDepartment(department);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const deleteSubDepartment = createAsyncThunk<
  void,
  number,
  { state: RootState }
>(
  "departments/deleteSubDepartment",
  async (subDepartmentId, { rejectWithValue }) => {
    const departmentService = new DepartmentService();
    try {
      await departmentService.deleteSubDepartment(subDepartmentId);
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export const deleteDepartment = createAsyncThunk<
  void,
  number,
  { state: RootState }
>("departments/deleteDepartment", async (departmentId, { rejectWithValue }) => {
  const departmentService = new DepartmentService();
  try {
    await departmentService.deleteDepartment(departmentId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const createDepartment = createAsyncThunk<
  ApplicationDepartment,
  { name: string; productSelectionMode: string; orderTemplate: string },
  { state: RootState }
>(
  "departments/create",
  async ({ name, productSelectionMode, orderTemplate }, { rejectWithValue }) => {
    const departmentService = new DepartmentService();
    try {
      return await departmentService.createDepartment(name, productSelectionMode, mapStringToEnum(ApplicationOrderTemplate, orderTemplate));
    } catch (error: any) {
      const apiError = ApplicationError.handleApiError(error, {});
      return rejectWithValue(apiError);
    }
  }
);

export interface UpdateDepartmentPayload {
  field: keyof ApplicationDepartment;
  value: any;
}

export interface UpdateSubDepartmentPayload {
  id: number,
  field: keyof ApplicationSubDepartment;
  value: any;
}

const adminDepartmentSlice = createSlice({
  name: "adminDepartment",
  initialState,
  reducers: {
    setSelectedSubDepartment: (
      state,
      action: PayloadAction<ApplicationSubDepartment | null>
    ) => {
      if (action.payload) {
        state.selectedSubDepartment = action.payload;
        state.creatingSubDepartment = false;
      } else {
        state.selectedSubDepartment = null;
      }
    },
    setNewSubDepartment(state) {
      const departmentId = state.selectedDepartment?.id ?? 0;
      state.selectedSubDepartment = {
        id: 0,
        departmentId,
        name: "",
        productSelectionMode: ApplicationProductSelectionMode.AddOn,
        description: "",
        active: false,
        canDelete: false,
      };
      state.creatingSubDepartment = true;
    },
    updateSubDepartmentField(state, action: PayloadAction<UpdateSubDepartmentPayload>) {
      const { field, value } = action.payload;
      let subDepartment = state.selectedSubDepartment;

      if (subDepartment) {
        (subDepartment as any)[field] = value;
      }

    },
    remooveSubDepartment: (state, action: PayloadAction<number>) => {
      if (state.selectedDepartment) {
        state.selectedDepartment.subDepartments =
          state.selectedDepartment.subDepartments?.filter(
            (c) => c.id !== action.payload
          ) ?? [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentById.fulfilled, (state, action) => {
        state.selectedDepartment = action.payload;
        state.loading = false;
      })
      .addCase(fetchDepartmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.selectedDepartment = action.payload;
        state.selectedSubDepartment = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.selectedDepartment = action.payload;
        state.loading = false;
        state.error = null;
      });
  },
});

export const {
  updateSubDepartmentField,
  setSelectedSubDepartment,
  setNewSubDepartment,
  remooveSubDepartment,
} = adminDepartmentSlice.actions;

export default adminDepartmentSlice.reducer;
